import React, { useEffect, useState, useRef } from 'react';
import LoadError from '../../components/LoadError';
import { formatDate, formatDateTime, profileTypeLabel } from '../../lib/utils';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, apiUpload, apiDownload } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/AuthContext';
import WorkflowTracker, { StatusBadge } from '../../components/WorkflowTracker';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import {
  ArrowLeft, Pencil, Trash2, Upload, FileText, Download,
  Clock, User, Mail, Phone, MapPin, BookOpen, GraduationCap, Globe, Hash, Award,
  Plus, Building2, CheckCircle2, Check, ChevronsUpDown, Eye, Info
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentPreview, { downloadDocument } from '../../components/DocumentPreview';
import CourseCombobox from '../../components/CourseCombobox';
import { docTypeNameProblem, expectedTypeFileName } from '../../lib/docNaming';
import PassportPortfolio from '../../components/passport/PassportPortfolio';

const STATUSES = ['lead', 'documents_submitted', 'under_review', 'offer_letter', 'visa_processing', 'enrolled', 'rejected'];

// Exported so other pages (e.g. ApplicationDetail, which shows the
// candidate's required-document checklist alongside an application) can
// reuse the exact same checklist definitions instead of duplicating them.
export const DOC_TYPES_BACHELORS = [
  { value: '10th_certificate', label: '10th Class Certificate' },
  { value: '12th_certificate', label: '12th / Intermediate Certificate' },
  { value: 'english_scorecard', label: 'English Test Scorecard (IELTS/Duolingo/TOEFL/PTE)' },
  { value: 'passport', label: 'Passport (Front & Address Page)' },
  { value: 'resume', label: 'Resume' },
  { value: 'sop', label: 'Statement of Purpose' },
];

export const DOC_TYPES_MASTERS_PHD = [
  { value: '10th_certificate', label: '10th Class Certificate' },
  { value: '12th_certificate', label: '12th / Intermediate Certificate' },
  { value: 'ug_certificates', label: 'UG Certificates (All Transcripts, Consolidated Marks Memo, Provisional & Original Degree, All Sem Memos)' },
  { value: 'lor', label: 'Letters of Recommendation (minimum 3)', min: 3 },
  { value: 'sop', label: 'Statement of Purpose - Xpert Advisor' },
  { value: 'english_scorecard', label: 'English Test Scorecard (IELTS/Duolingo/TOEFL/PTE)' },
  { value: 'passport', label: 'Passport (Front & Address Page)' },
  { value: 'resume', label: 'Resume' },
  { value: 'moi', label: 'Medium of Instruction - From College' },
  { value: 'no_backlog_certificate', label: 'No Backlog Certificate - From College' },
];

// Separate upload types used before UG certificates and LORs were grouped into one
// section each. Files already uploaded under them still count toward their group.
export const DOC_GROUPS = {
  ug_certificates: ['all_transcripts', 'consolidated_marks_memo', 'provisional_certificate', 'original_degree', 'sem_memos'],
  lor: ['lor_1', 'lor_2', 'lor_3'],
};
const LEGACY_DOC_LABELS = {
  all_transcripts: 'UG Certificates · All Transcripts',
  consolidated_marks_memo: 'UG Certificates · Consolidated Marks Memo',
  provisional_certificate: 'UG Certificates · Provisional Certificate',
  original_degree: 'UG Certificates · Original Degree',
  sem_memos: 'UG Certificates · Sem Memos',
  lor_1: 'Letter of Recommendation 1',
  lor_2: 'Letter of Recommendation 2',
  lor_3: 'Letter of Recommendation 3',
};
export const docGroupOf = (type) => Object.keys(DOC_GROUPS).find(g => DOC_GROUPS[g].includes(type)) || type;
export const docTypeLabel = (types, value) =>
  types.find(d => d.value === value)?.label || LEGACY_DOC_LABELS[value] || value?.replace(/_/g, ' ');
// How many files a checklist item has, and whether it's complete (LORs need 3).
export const checklistItem = (docs, dt) => {
  const count = docs.filter(d => docGroupOf(d.doc_type) === dt.value).length;
  return { count, done: count >= (dt.min || 1) };
};

// Shows a stored score with its unit, e.g. "85%" or "8.5 CGPA"; older free-text values unchanged.
export const formatScore = (value, type) => {
  if (!value) return value;
  if (type === 'cgpa' && !/cgpa/i.test(value)) return `${value} CGPA`;
  if (type === 'percentage' && !value.includes('%')) return `${value}%`;
  return value;
};

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin' : '/partner';
  const fileInputRef = useRef(null);

  const [candidate, setCandidate] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('10th_certificate');
  const [applications, setApplications] = useState([]);
  const [appDialog, setAppDialog] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [selectedUniId, setSelectedUniId] = useState('');
  const [uniPopoverOpen, setUniPopoverOpen] = useState(false);
  const [appCountry, setAppCountry] = useState('');
  const [appCourseName, setAppCourseName] = useState('');
  const [appIntake, setAppIntake] = useState('');
  const [appNote, setAppNote] = useState('');
  const [submittingApp, setSubmittingApp] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [zipping, setZipping] = useState(false);

  // After an upload/delete/status change we refresh quietly, so the page doesn't blank out and jump to the top.
  const fetchCandidate = (initial = false) => {
    if (initial) { setLoading(true); setLoadError(''); }
    Promise.all([
      apiGet(`/candidates/${id}`),
      apiGet(`/candidates/${id}/documents`),
    ]).then(([cRes, dRes]) => {
      setCandidate(cRes.data);
      setDocuments(dRes.data);
      // Fetch applications separately so a failure doesn't block the page
      apiGet(`/candidates/${id}/applications`).then(r => setApplications(r.data)).catch(() => {});
    }).catch(err => {
      if (!initial) { toast.error("Couldn't refresh the page. Reload to see the latest data."); return; }
      if (err.response?.status === 404) setCandidate(null);
      else setLoadError("We couldn't load this candidate. Check your connection and try again.");
    }).finally(() => setLoading(false));
  };

  const fetchUniversities = () => {
    if (universities.length) return;
    apiGet('/universities').then(r => setUniversities(r.data)).catch(() => {});
  };

  const handleAddApplication = async () => {
    if (!selectedUniId) return;
    setSubmittingApp(true);
    try {
      const res = await apiPost('/applications', { candidate_id: id, university_id: selectedUniId, course_name: appCourseName, intake: appIntake, notes: appNote });
      setApplications(prev => [res.data, ...prev]);
      toast.success(`Application ${res.data.application_number} created`);
      setAppDialog(false);
      setSelectedUniId('');
      setUniPopoverOpen(false);
      setAppCountry('');
      setAppCourseName('');
      setAppIntake('');
      setAppNote('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create application');
    } finally { setSubmittingApp(false); }
  };

  const handleDeleteApplication = async (appId, appNum) => {
    if (!window.confirm(`Delete application ${appNum}?`)) return;
    try {
      await apiDelete(`/applications/${appId}`);
      setApplications(prev => prev.filter(a => a.id !== appId));
      toast.success('Application removed');
    } catch { toast.error('Failed to delete'); }
  };

  useEffect(() => { fetchCandidate(true); }, [id]);
  useEffect(() => {
    if (loading || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [loading, location.hash]);

  // Landing here right after "Register Candidate" or "Apply" on the New
  // Candidate form (both navigate with state: { openApply: true }) should
  // drop the partner straight into the "Add University Application" flow
  // instead of just the bare profile page. Clear the flag immediately after
  // so a refresh or navigating back here doesn't reopen the dialog.
  useEffect(() => {
    if (location.state?.openApply) {
      fetchUniversities();
      setAppDialog(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleStatusUpdate = async () => {
    if (!newStatus || savingStatus) return; // a double click would add two history entries
    setSavingStatus(true);
    try {
      await apiPost(`/candidates/${id}/status`, { status: newStatus, note: statusNote });
      toast.success('Status updated');
      setStatusDialog(false);
      setNewStatus('');
      setStatusNote('');
      fetchCandidate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
    finally { setSavingStatus(false); }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    // Only files named after the selected document type are accepted; check them all before uploading any.
    const problems = Array.from(files).map(f => docTypeNameProblem(f.name, docType)).filter(Boolean);
    if (problems.length) {
      toast.error(problems[0] + (problems.length > 1 ? ` (${problems.length - 1} more file(s) also need renaming)` : ''), { duration: 8000 });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await apiUpload(`/candidates/${id}/documents?doc_type=${docType}`, formData);
      }
      toast.success(`${files.length} document(s) uploaded`);
      fetchCandidate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDownload = (doc) => downloadDocument(doc);

  const handleDownloadAll = async () => {
    setZipping(true);
    try {
      const res = await apiDownload(`/candidates/${id}/documents/zip`);
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(candidate.name || 'candidate').replace(/[^\w-]+/g, '_')}_Documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    finally { setZipping(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await apiDelete(`/documents/${docId}`);
      toast.success('Document deleted');
      fetchCandidate();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this candidate permanently?')) return;
    try {
      await apiDelete(`/candidates/${id}`);
      toast.success('Candidate deleted');
      navigate(`${basePath}/candidates`);
    } catch { toast.error('Failed to delete'); }
  };

  const getDocTypeLabel = (value) => docTypeLabel(DOC_TYPES, value);

  const uploadedDocTypes = documents.map(d => docGroupOf(d.doc_type));
  // Documents uploaded from an application (they carry an application_id)
  // are shown separately under "Supporting Documents" instead of mixing
  // into the candidate's Required Documents checklist list.
  const checklistDocuments = documents.filter(d => !d.application_id);
  const supportingDocuments = documents.filter(d => d.application_id);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>;
  if (loadError) return <LoadError what="this candidate" onRetry={() => fetchCandidate(true)} />;
  if (!candidate) return <div className="text-center py-12 text-ink-2">Candidate not found</div>;

  const profileType = candidate.profile_type || 'masters';
  const DOC_TYPES = profileType === 'bachelors' ? DOC_TYPES_BACHELORS : DOC_TYPES_MASTERS_PHD;
  const profileLabel = profileTypeLabel(profileType);
  const profileColor = profileType === 'phd' ? 'bg-brand/10 text-iris' : profileType === 'masters' ? 'bg-surface-3 text-brand' : 'bg-surface-3 text-aqua';
  const showBachelor = profileType === 'masters' || profileType === 'phd';
  const showMasters = profileType === 'phd';

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="candidate-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`${basePath}/candidates`)} aria-label="Back to candidates" className="p-2 text-ink-2 hover:text-ink rounded-lg hover:bg-contrast/[0.07]" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink" style={{ fontFamily: 'Outfit' }}>{candidate.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              {candidate.xpert_id && <span className="text-xs font-mono font-semibold text-brand bg-surface-3 px-2.5 py-1 rounded">{candidate.xpert_id}</span>}
              <StatusBadge status={candidate.status} />
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${profileColor}`}>
                {profileLabel} Profile
              </span>
              {isAdmin && candidate.partner_name && <span className="text-sm text-ink-2">Partner: {candidate.partner_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button variant="outline" onClick={() => setStatusDialog(true)} data-testid="update-status-btn" className="gap-2">
              <Clock className="w-4 h-4" /> Update Status
            </Button>
          )}
          {!isAdmin && (
            <>
              <Link to={`/partner/candidates/${id}/edit`}>
                <Button variant="outline" data-testid="edit-candidate-btn" className="gap-2"><Pencil className="w-4 h-4" /> Edit</Button>
              </Link>
              <Button variant="outline" onClick={handleDelete} data-testid="delete-candidate-btn" className="text-ember hover:text-danger hover:bg-ember/10 gap-2">
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Workflow Tracker */}
      <section id="applications"><PassportPortfolio candidate={candidate} applications={applications} documents={documents} basePath={basePath} onAdd={() => { setAppDialog(true); fetchUniversities(); }} onDelete={handleDeleteApplication}/></section>
      <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-6">
        <h2 className="text-lg font-semibold text-ink mb-4" style={{ fontFamily: 'Outfit' }}>Candidate Journey</h2>
        <WorkflowTracker currentStatus={candidate.status} statusHistory={candidate.status_history} />
      </div>

      {/* Student Info Grid */}
      <div id="student-information" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Student Information */}
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-4">Student Information</h3>
          <div className="space-y-3">
            <InfoRow icon={User} label="Name" value={candidate.name} />
            <InfoRow icon={Mail} label="Email" value={candidate.email} />
            <InfoRow icon={Phone} label="Phone" value={candidate.phone} />
            <InfoRow icon={MapPin} label="Country" value={candidate.country} />
            <InfoRow icon={Globe} label="Nationality" value={candidate.nationality} />
            <InfoRow icon={FileText} label="Passport" value={candidate.passport_number} />
            <InfoRow icon={Clock} label="Intake" value={candidate.semester_intake_year} />
            <InfoRow icon={BookOpen} label="Course Interested" value={candidate.course_interested} />
          </div>
        </div>

        {/* Academic Details */}
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-4">Academic Details</h3>
          <div className="space-y-3">
            <InfoRow icon={BookOpen} label="10th Year" value={candidate.tenth_completion_year} />
            <InfoRow icon={Hash} label="10th Score" value={formatScore(candidate.tenth_percentage, candidate.tenth_score_type)} />
            <InfoRow icon={BookOpen} label="12th / Intermediate Course & Year" value={candidate.inter_course_year} />
            <InfoRow icon={Hash} label="12th / Intermediate Score" value={formatScore(candidate.inter_percentage, candidate.inter_score_type)} />
            <InfoRow icon={Hash} label="12th / Intermediate English %" value={candidate.inter_english_percentage} />

            {showBachelor && (
              <>
                <div className="border-t border-contrast/10 my-2" />
                <InfoRow icon={GraduationCap} label="Bachelor Course" value={candidate.bachelor_course_name} />
                <InfoRow icon={Clock} label="Bachelor Year" value={candidate.bachelor_completion_year} />
                <InfoRow icon={Hash} label="Bachelor % / CGPA" value={formatScore(candidate.bachelor_percentage, candidate.bachelor_score_type)} />
                <InfoRow icon={Hash} label="Total Backlogs" value={candidate.total_backlogs} />
              </>
            )}

            {showMasters && (
              <>
                <div className="border-t border-contrast/10 my-2" />
                <InfoRow icon={Award} label="Masters Course" value={candidate.masters_course_name} />
                <InfoRow icon={Clock} label="Masters Year" value={candidate.masters_completion_year} />
                <InfoRow icon={Hash} label="Masters % / CGPA" value={formatScore(candidate.masters_percentage, candidate.masters_score_type)} />
              </>
            )}

            {!showBachelor && <InfoRow icon={Hash} label="Total Backlogs" value={candidate.total_backlogs} />}
            <div className="border-t border-contrast/10 my-2" />
            <InfoRow icon={FileText} label="English Test" value={candidate.english_test_type ? `${candidate.english_test_type}: ${candidate.english_test_score || '-'}` : candidate.english_test_score} />
            <InfoRow icon={Hash} label="Listening" value={candidate.english_listening} />
            <InfoRow icon={Hash} label="Reading" value={candidate.english_reading} />
            <InfoRow icon={Hash} label="Writing" value={candidate.english_writing} />
            <InfoRow icon={Hash} label="Speaking" value={candidate.english_speaking} />
          </div>
        </div>
      </div>

      {/* Notes */}
      {candidate.notes && (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-3">Notes</h3>
          <p className="text-sm text-ink-2">{candidate.notes}</p>
        </div>
      )}

      {/* Documents Section */}
      <div id="documents" className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3">Required Documents ({DOC_TYPES.filter(dt => checklistItem(checklistDocuments, dt).done).length}/{DOC_TYPES.length})</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {documents.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDownloadAll} disabled={zipping} data-testid="download-all-docs-btn" className="gap-2 shrink-0">
                <Download className="w-4 h-4" /> {zipping ? 'Preparing...' : 'Download All'}
              </Button>
            )}
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="w-80 h-9 text-xs" data-testid="doc-type-select" aria-label="Document type to upload">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-2">
                      {uploadedDocTypes.includes(t.value) && <span className="w-2 h-2 rounded-full bg-brand shrink-0" />}
                      {t.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-document-btn" className="gap-2 shrink-0">
              <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload'}
            </Button>
            <input ref={fileInputRef} type="file" multiple accept={['passport','passport_front','passport_address'].includes(docType) ? 'image/*,.pdf' : docType === 'resume' ? '.pdf,.doc,.docx' : '.pdf,image/*'} className="hidden" onChange={handleFileUpload} data-testid="file-input" />
          </div>
        </div>

        {/* Naming convention */}
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-brand/30 bg-surface-3/60 text-xs text-ink-2" data-testid="doc-naming-note">
          <Info className="w-4 h-4 text-brand shrink-0 mt-0.5" />
          <span>
            Before uploading, name each file after its <span className="font-semibold text-ink">document type</span>, e.g.{' '}
            <span className="font-mono text-ink">Passport.pdf</span>.
            For several files of one type, add a number: <span className="font-mono text-ink">LOR1</span>, <span className="font-mono text-ink">LOR2</span>.
            {' '}For the selected document: <span className="font-mono text-ink" data-testid="doc-expected-name">{expectedTypeFileName(docType)}.pdf</span>. Files named any other way are not accepted.
          </span>
        </div>

        {/* Document Checklist */}
        <div className="mb-4 p-4 bg-contrast/5 rounded-lg border border-contrast/10">
          <h4 className="text-sm font-semibold text-ink mb-3">Document Checklist</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6">
            {DOC_TYPES.map((dt, idx) => {
              const { count, done: isUploaded } = checklistItem(checklistDocuments, dt);
              return (
                <div key={dt.value} className="flex items-center gap-2 text-sm" data-testid={`doc-checklist-${dt.value}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${isUploaded ? 'bg-brand/15 text-success' : 'bg-contrast/10 text-ink-2'}`}>
                    {isUploaded ? '✓' : idx + 1}
                  </span>
                  <span className={`flex-1 ${isUploaded ? 'text-ink' : 'text-ink-2'}`}>{dt.label}</span>
                  {dt.min ? (
                    <span className="text-xs bg-surface-3 text-brand px-1.5 py-0.5 rounded font-medium">{count}/{dt.min}</span>
                  ) : count > 1 && <span className="text-xs bg-surface-3 text-brand px-1.5 py-0.5 rounded font-medium">{count}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Uploaded Documents */}
        {checklistDocuments.length > 0 && (
          <div className="space-y-2" data-testid="documents-list">
            {checklistDocuments.map((doc) => (
              <div key={doc.id} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-contrast/5 rounded-lg hover:bg-contrast/[0.07] transition-colors" data-testid={`document-${doc.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText className="w-5 h-5 text-brand shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{doc.original_filename}</p>
                    <p className="text-xs text-ink-3">{getDocTypeLabel(doc.doc_type)} &middot; {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''} &middot; {formatDate(doc.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setPreviewDoc(doc)} title="Preview" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg" data-testid={`preview-doc-${doc.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDownload(doc)} title="Download" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg" data-testid={`download-doc-${doc.id}`}>
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteDoc(doc.id)} title="Delete" aria-label="Delete document" className="p-2 text-ink-2 hover:text-danger hover:bg-contrast/[0.06] rounded-lg" data-testid={`delete-doc-${doc.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Supporting Documents (uploaded from an application) */}
        {supportingDocuments.length > 0 && (
          <div className="mt-6 pt-4 border-t border-contrast/10" data-testid="supporting-documents-section">
            <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-3">Supporting Documents</h3>
            <div className="space-y-2" data-testid="supporting-documents-list">
              {supportingDocuments.map((doc) => (
                <div key={doc.id} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-contrast/5 rounded-lg hover:bg-contrast/[0.07] transition-colors" data-testid={`supporting-document-${doc.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-brand shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{doc.original_filename}</p>
                      <p className="text-xs text-ink-3">{getDocTypeLabel(doc.doc_type)} &middot; {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''} &middot; {formatDate(doc.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setPreviewDoc(doc)} title="Preview" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg" data-testid={`preview-supporting-doc-${doc.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDownload(doc)} title="Download" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg" data-testid={`download-supporting-doc-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteDoc(doc.id)} title="Delete" aria-label="Delete document" className="p-2 text-ink-2 hover:text-danger hover:bg-contrast/[0.06] rounded-lg" data-testid={`delete-supporting-doc-${doc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Application Dialog */}
      <Dialog open={appDialog} onOpenChange={setAppDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Add University Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label htmlFor="cd-app-country" className="text-sm font-medium text-ink">Country</label>
              <Select
                value={appCountry || '__all__'}
                onValueChange={(v) => setAppCountry(v === '__all__' ? '' : v)}
              >
                <SelectTrigger id="cd-app-country" className="bg-surface text-ink border-contrast/10">
                  <SelectValue placeholder="All countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All countries</SelectItem>
                  {[...new Set(universities.map(u => u.country).filter(Boolean))]
                    .sort((a, b) => a.localeCompare(b))
                    .map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-ink-3">Narrows the university list below — pick a country first to cut down the search.</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="cd-app-university" className="text-sm font-medium text-ink">Select University *</label>
              <Popover open={uniPopoverOpen} onOpenChange={setUniPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    id="cd-app-university"
                    type="button"
                    role="combobox"
                    aria-expanded={uniPopoverOpen}
                    className="w-full flex items-center justify-between rounded-md border border-contrast/10 bg-surface px-3 py-2 text-sm text-left focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                  >
                    <span className={`truncate ${selectedUniId ? 'text-ink' : 'text-ink-3'}`}>
                      {(() => {
                        const u = universities.find(u => u.id === selectedUniId);
                        return u ? `${u.name}${u.country ? ` — ${u.country}` : ''}` : 'Choose a university...';
                      })()}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-ink-3 shrink-0 ml-2" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-0" align="start">
                  <Command
                    filter={(value, search) => {
                      if (!search) return 1;
                      const terms = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
                      const target = value.toLowerCase();
                      return terms.every(t => target.includes(t)) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Search by university name or country..." />
                    <CommandList>
                      <CommandEmpty>No university found.</CommandEmpty>
                      <CommandGroup>
                        {universities
                          .filter(u => !applications.some(a => a.university_id === u.id))
                          .filter(u => !appCountry || u.country === appCountry)
                          .map(u => (
                            <CommandItem
                              key={u.id}
                              value={`${u.name} ${u.country || ''} ${u.city || ''}`}
                              onSelect={() => { setSelectedUniId(u.id); setUniPopoverOpen(false); }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${selectedUniId === u.id ? 'opacity-100' : 'opacity-0'}`} />
                              {u.name} {u.country ? `— ${u.country}` : ''}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {applications.length > 0 && (
                <p className="text-xs text-ink-3">
                  Already applied: {applications.map(a => a.university_name).join(', ')}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label htmlFor="app-course-select" className="text-sm font-medium text-ink">Course Name</label>
                <CourseCombobox
                  value={appCourseName}
                  onChange={setAppCourseName}
                  params={selectedUniId ? { university_id: selectedUniId } : { country: appCountry }}
                  placeholder="Select course..."
                  testId="app-course-select"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="cd-appIntake" className="text-sm font-medium text-ink">Intake</label>
                <input id="cd-appIntake"
                  value={appIntake}
                  onChange={e => setAppIntake(e.target.value)}
                  placeholder="e.g. Sep 2025"
                  className="w-full rounded-md border border-contrast/10 bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="cd-appNote" className="text-sm font-medium text-ink">Notes (optional)</label>
              <textarea id="cd-appNote"
                value={appNote}
                onChange={e => setAppNote(e.target.value)}
                placeholder="Any notes about this application..."
                rows={2}
                className="w-full rounded-md border border-contrast/10 bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 resize-none focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAppDialog(false)}>Cancel</Button>
              <Button
                onClick={handleAddApplication}
                disabled={!selectedUniId || submittingApp}
                className="bg-brand hover:bg-brand-soft text-white gap-1.5"
              >
                <CheckCircle2 className="w-4 h-4" />
                {submittingApp ? 'Creating...' : 'Create Application'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DocumentPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />

      {/* Status History */}
      {candidate.status_history?.length > 0 && (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-4">Status History</h3>
          <div className="space-y-3" data-testid="status-history">
            {[...candidate.status_history].reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-brand mt-2 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={h.status} />
                    <span className="text-ink-3 text-xs">{formatDateTime(h.timestamp)}</span>
                  </div>
                  {h.note && <p className="text-ink-2 mt-1">{h.note}</p>}
                  {h.updated_by && <p className="text-xs text-ink-3">by {h.updated_by}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Update Status Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Update Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label htmlFor="cd-new-status-select" className="text-sm font-medium text-ink">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger id="cd-new-status-select" data-testid="new-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="cd-status-note-input" className="text-sm font-medium text-ink">Note (optional)</label>
              <Textarea id="cd-status-note-input" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} data-testid="status-note-input" placeholder="Add a note about this status change..." rows={3} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
              <Button onClick={handleStatusUpdate} disabled={!newStatus || savingStatus} data-testid="confirm-status-btn" className="bg-brand hover:bg-brand-soft text-white">
                {savingStatus ? 'Updating…' : 'Update Status'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-ink-3 shrink-0" />
      <span className="text-sm text-ink-2 w-36 shrink-0">{label}</span>
      <span className="text-sm text-ink font-medium min-w-0 break-words">{value || '-'}</span>
    </div>
  );
}
